<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Globe, Phone, Share2, MapPin, Users } from 'lucide-svelte';

  import { getRoleLabel } from '$lib/utils/roleMapping';
  import { contactRoles, embeddingQuery } from '$lib/stores/selection';
  import { applicationMode } from '$lib/stores/mode';
  import { isRanked, scoreBand, scorePercent } from '$lib/utils/score';
  import AddToListMenu from '$lib/components/AddToListMenu.svelte';
  import type { CompanyWithContacts } from '$lib/types/api';
  import type { MemberRow } from '$lib/api/memberrows';

  /**
   * One company in a signed-in customer's result list.
   *
   * # What it shows, and what it does not
   *
   * The name, the size, what the website says the company does, where it is,
   * which language it corresponds in, how many senior people are known and of
   * what roles, how many others, and whether it can be reached by web, phone or
   * a social profile.
   *
   * Not one person's name, address or telephone number. Those are the profile's
   * to give, and the profile is the route fair use counts — see
   * `docs/refactor/member-result-row.md`. A list that carried them would be the
   * uncharged bulk channel this row exists to replace.
   *
   * # Why it is a second component rather than a flag on ResultItem
   *
   * ResultItem draws the public row and keeps doing so. One component drawing
   * two different rows depending on a store is the shape that produced two
   * bugs in a single session — the front screen and the industry picker — and
   * the shells can simply choose.
   */
  export let result: CompanyWithContacts & Partial<MemberRow>;

  /** Unused here; accepted so the two row components take the same props. */
  export let showSimilarPanel = false;

  $: roles = result.roles ?? [];
  $: contacts = result.contact_count ?? 0;

  /**
   * Where it is, read as an address: postcode and town, then the canton.
   *
   * The postcode leads because it is what somebody working a region scans for,
   * and it disambiguates the towns that share a name. A company missing any
   * part reads better with nothing than with a stray comma.
   */
  $: place = [[result.zip, result.city].filter(Boolean).join(' '), result.canton]
    .filter(Boolean)
    .join(', ');

  /**
   * How many people are known but not placed in a department.
   *
   * Most of the corpus: 1.66 million contacts carry no harmonized role against
   * 753,000 who do. Showing the total without saying so would read as a list
   * that had lost rows.
   */
  $: unplaced = Math.max(0, contacts - roles.reduce((n, r) => n + r.count, 0));

  /**
   * Which roles the customer asked for.
   *
   * A role they filtered on and we found is the answer to their question, so it
   * is marked. The rest are what else is there, which is the reason to look
   * rather than the reason it matched.
   */
  $: wanted = new Set($contactRoles ?? []);

  /**
   * How well this company answered a semantic search.
   *
   * Shown only when there was one to answer: the similar-companies screen
   * always ranks, and classic mode ranks when a description was given. A
   * filtered selection is unranked — every row comes back -1 — and a column of
   * badges saying so would be noise where the answer is "you did not ask".
   *
   * The bands are shared with the public row rather than copied. They are a
   * judgement about what counts as a good match, and two lists disagreeing
   * about the same company on the same search is the failure that matters.
   */
  $: searchWasRanked =
    $applicationMode === 'neighbor' || ($applicationMode === 'classic' && !!$embeddingQuery.trim());
  $: showScore = searchWasRanked && isRanked(result.quality);
</script>

<article
  class="group rounded-surface border border-hairline bg-white p-4 transition-colors hover:border-accent-300"
  class:ring-1={showSimilarPanel}
>
  <!--
    The name alone on the first line, with what can be done to the company
    beside it.

    It shared a line with the place, the indicators and two hover controls, and
    the name — the thing being scanned for — was the first casualty when a long
    one met a long town. The lines below carry the rest, in the order a customer
    reads them: where it is, what it does, who is there, how to reach it.
  -->
  <div class="flex items-start gap-2">
    {#if showScore}
      <span
        class="mt-0.5 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold {scoreBand(
          result.quality
        )}"
        title={$_('memberRow.matchScore')}
      >
        {scorePercent(result.quality)}%
      </span>
    {/if}

    <h3 class="min-w-0 flex-1 text-base font-semibold leading-snug text-gray-900">
      <!--
        The name is the link, and the only one.

        There was a hover-revealed "Profil" beside it doing the same thing —
        two controls for one action, the second discoverable only by hovering.
        Clicking a company's name to open it is what a list of companies
        already means.
      -->
      <a
        href="/company/{result.id}"
        class="transition-colors hover:text-accent-700 hover:underline group-hover:text-accent-700"
      >
        {result.name}
      </a>
    </h3>

    <!--
      Into a list. Company only: this row deliberately shows role counts and no
      names — names live behind the metered profile door — so there is no
      individual person here to add.
    -->
    <span
      class="shrink-0 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100"
    >
      <AddToListMenu records={() => [{ company_id: result.id }]} count={1} />
    </span>
  </div>

  <!-- Where it is, read as an address is: postcode, town, canton. -->
  {#if place}
    <p class="mt-0.5 flex items-center gap-1 text-sm text-gray-500">
      <MapPin class="h-3.5 w-3.5 shrink-0" />{place}
    </p>
  {/if}

  <!--
    What the company says it offers, every heading its site leads with rather
    than only the first. Two lines: enough for a handful, and a firm with eight
    does not push the roles below it off the card.
  -->
  {#if result.web_summary}
    <p class="mt-2 line-clamp-2 text-sm text-gray-600">{result.web_summary}</p>
  {/if}

  <!--
    Who is there, by role.

    The roles rather than a count, because the role is what a customer chose on:
    somebody who filtered for sales wants to see that this company has sales.
    The name is not here — that is the profile's to give, and the profile is the
    route fair use counts.

    A role the customer filtered for is marked, because it is the answer to
    their question rather than merely what else is there.
  -->
  {#if roles.length > 0 || unplaced > 0}
    <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
      {#each roles as role (role.role)}
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium {wanted.has(
            role.role
          )
            ? 'bg-accent-50 text-accent-700 ring-1 ring-accent-200'
            : 'bg-surface-sunken text-gray-600'}"
          title={wanted.has(role.role) ? $_('memberRow.roleWanted') : undefined}
        >
          <span
            class="h-1.5 w-1.5 rounded-full {wanted.has(role.role)
              ? 'bg-accent-600'
              : 'bg-gray-400'}"
          ></span>
          {getRoleLabel(role.role, $_)}
          {#if role.count > 1}
            <span class="tabular-nums opacity-60">{role.count}</span>
          {/if}
        </span>
      {/each}

      {#if unplaced > 0}
        <!-- Known, but never placed in a department. Said plainly rather than
             folded into a total that would not add up. -->
        <span class="inline-flex items-center gap-1 text-gray-400">
          <Users class="h-3.5 w-3.5" />
          {$_('memberRow.unplaced', { values: { count: unplaced } })}
        </span>
      {/if}
    </div>
  {/if}

  <!--
    The size and what the company can be reached by, at the foot — the public
    row's arrangement, and the same coloured pills, so the two lists read as one
    product seen from two accounts.

    Presence, not the values: a channel is not what an account buys, and the
    numbers themselves are on the profile.
  -->
  <div class="mt-3 flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
    {#if result.size}
      <span
        class="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-300/50"
      >
        <Users class="h-3 w-3" />
        {result.size}
      </span>
    {/if}

    {#if result.language}
      <span
        class="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium uppercase text-gray-600 ring-1 ring-inset ring-gray-300/50"
      >
        {result.language}
      </span>
    {/if}

    {#if result.has_web}
      <span
        class="inline-flex items-center rounded-full bg-accent-50 px-2.5 py-1 text-accent-700 ring-1 ring-inset ring-accent-700/10"
        title={$_('memberRow.hasWeb')}
      >
        <Globe class="h-4 w-4" />
      </span>
    {/if}

    {#if result.has_phone}
      <span
        class="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700 ring-1 ring-inset ring-emerald-700/10"
        title={$_('memberRow.hasPhone')}
      >
        <Phone class="h-4 w-4" />
      </span>
    {/if}

    {#if result.has_social}
      <span
        class="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-blue-700 ring-1 ring-inset ring-blue-700/10"
        title={$_('memberRow.hasSocial')}
      >
        <Share2 class="h-4 w-4" />
      </span>
    {/if}
  </div>
</article>
